import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
} from "reactstrap";
import { Eye } from "react-feather";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";

import TargetAssignedOne from "./TargetAssingnedOne"; // ← your existing component
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import { _Get } from "../import/ApiEndPoint/ApiCalling";
import {
  View_CustomerTarget_List,
  view_create_order_history,
} from "../import/ApiEndPoint/Api";
import {
  exportDataToPDF,
  exportDataToExcel,
  convertDataCSVtoExcel,
  convertDataCsvToXml,
} from "../import/Downloader";

/* ---------- tiny page-scoped CSS (no external SCSS) ---------- */
const injectOnce = () => {
  if (document.getElementById("ta-inline-css")) return;
  const style = document.createElement("style");
  style.id = "ta-inline-css";
  style.innerHTML = `
    .ta-card{ border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .ta-card-light{ border:1px solid #e5e7eb; border-radius:14px; background:#f8fafc; }
    .ta-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .ta-table{ width:100%; border-collapse:collapse; min-width:1080px; }
    .ta-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .ta-table tbody td{ padding:8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; text-align:center; font-size:14px; }
    .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .dl-menu{ position:absolute; right:0; top:32px; z-index:10; border:1px solid rgb(8,91,245); background:#fff; border-radius:10px;
      overflow:hidden; min-width:140px; box-shadow:0 12px 24px rgba(0,0,0,.12); }
    .dl-item{ padding:10px 12px; cursor:pointer; font-weight:600; letter-spacing:.02em; }
    .dl-item:hover{ background:#f4f7f6; }
    .muted{ color:#64748b; }
  `;
  document.head.appendChild(style);
};

/* ---------- helpers ---------- */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const num = (v) => (isFinite(+v) ? +v : 0);

const monthNameToIndex = (label /* e.g. "January-2025" */) => {
  if (!label) return null;
  const [m, y] = String(label).split("-"); // "Month-YYYY"
  const idx = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ].findIndex((x) => x.toLowerCase() === String(m).toLowerCase());
  if (idx < 0) return null;
  return { year: Number(y), month: idx + 1 }; // 1..12
};

const sameMonth = (d, y, m) => {
  if (!d) return false;
  const dt = new Date(d);
  return dt.getFullYear() === y && dt.getMonth() + 1 === m;
};

const partyKey = (id) =>
  typeof id === "object"
    ? String(id?.sId || id?._id || id?.id || "")
    : String(id || "");

/* ---------- page ---------- */
export default function TargetAchieved() {
  useEffect(() => injectOnce(), []);

  const [permissions, setPermissions] = useState({
    View: true,
    Edit: false,
    Delete: false,
  });
  const [masterShow, setMasterShow] = useState(false);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]); // enriched list
  const [search, setSearch] = useState("");

  // pagination (simple client-side)
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  // downloads
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  // modal
  const [modal, setModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const toggleModal = () => setModal((v) => !v);

  // super admin switch
  const reloadRef = useRef({ uid: null, db: null });
  const handleParentSubmit = async () => {
    const a = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null"
    );
    if (!a) return;
    const [uid, db] = String(a).split(" ");
    reloadRef.current = { uid, db };
    fetchData(uid, db);
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  // core fetch
  const fetchData = async (overrideUid, overrideDb) => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      setPermissions(CheckPermission("Target Creation") || {});
      setMasterShow(user?.rolename?.roleName === "MASTER");

      const db = overrideDb || user?.database;

      // 1) all targets
      const res = await _Get(View_CustomerTarget_List, db);
      const targetList = Array.isArray(res?.data) ? res.data : [];

      // 2) order history (completed, not deactive)
      const resSales = await _Get(
        view_create_order_history,
        overrideUid || user?._id,
        db
      );
      const completed = (resSales?.orderHistory || [])
        .filter((x) => x?.status !== "Deactive")
        .filter((x) => String(x?.status || "").toLowerCase() === "completed");

      // Enrich: for each target record, compute achieved for the same month as target.date (fallback: createdAt)
      const enriched = targetList.map((t) => {
        // Prefer `date` like "Month-YYYY"
        const parsed = monthNameToIndex(t?.date);
        let y, m;
        if (parsed) {
          y = parsed.year;
          m = parsed.month;
        } else if (t?.createdAt) {
          const d = new Date(t.createdAt);
          y = d.getFullYear();
          m = d.getMonth() + 1;
        }

        const pk = partyKey(t?.partyId);
        const monthOrders = completed.filter(
          (oh) => sameMonth(oh?.date, y, m) && partyKey(oh?.partyId) === pk // order.partyId may be object/string
        );

        // Build achieved per product (qty and amount)
        const achievedByProd = new Map(); // key: productId (sId or id)
        for (const ord of monthOrders) {
          for (const it of ord?.orderItems || []) {
            const k = String(it?.productId?.sId || it?.productId || "");
            if (!k) continue;
            const qty = num(it?.qty || it?.quantity);
            const price = num(it?.price) || num(it?.rate) || num(it?.mrp) || 0;
            const amt = qty * price;
            const prev = achievedByProd.get(k) || { qty: 0, amt: 0 };
            achievedByProd.set(k, { qty: prev.qty + qty, amt: prev.amt + amt });
          }
        }

        // merge into t.products
        const products = (t?.products || []).map((p) => {
          const key = String(p?.productDetails?.sId || p?.productId || "");
          const ach = achievedByProd.get(key) || { qty: 0, amt: 0 };
          const qty = num(p?.qtyAssign);
          const price = num(p?.price);
          const lineTotal =
            p?.totalPrice != null ? num(p.totalPrice) : qty * price;
          return {
            ...p,
            // keep originals
            totalPrice: lineTotal,
            // add computed
            achievedQty: ach.qty,
            achievedPrice: ach.amt,
          };
        });

        return { ...t, products };
      });

      // role filter: customer sees only their own
      const userRole = user?.rolename?.roleName;
      const finalRows =
        userRole === "Customer"
          ? enriched.filter((x) => partyKey(x?.partyId) === String(user?.sId))
          : enriched;

      setRows(finalRows);
      setPage(1);
    } catch (err) {
      console.error("[TargetAchieved] fetch error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // search + computed ui rows
  const uiRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = rows.map((r) => {
      const targetAmt = (r?.products || []).reduce(
        (a, p) => a + num(p?.totalPrice),
        0
      );
      const achievedAmt = (r?.products || []).reduce(
        (a, p) => a + num(p?.achievedPrice),
        0
      );
      return {
        ...r,
        _targetAmt: targetAmt,
        _achievedAmt: achievedAmt,
      };
    });
    if (!term) return list;
    const hit = (v) => toStr(v).toLowerCase().includes(term);
    return list.filter((r) => {
      const c = r?.customer || {};
      return (
        hit(c?.CompanyName) ||
        hit(c?.email) ||
        hit(c?.City) ||
        hit(c?.State) ||
        hit(r?.date) ||
        hit(r?._id)
      );
    });
  }, [rows, search]);

  // pagination slice
  const totalPages = Math.max(1, Math.ceil(uiRows.length / pageSize));
  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return uiRows.slice(start, start + pageSize);
  }, [uiRows, page, pageSize]);

  // downloads
  const buildCSV = () => {
    const head = [
      "UID",
      "TOTAL TARGET",
      "TARGET ACHIEVED",
      "GSTIN",
      "PARTY NAME",
      "EMAIL",
      "CITY",
      "STATE",
      "DATE",
      "CREATED AT",
      "UPDATED AT",
    ].join(",");
    const lines = uiRows.map((r, i) => {
      const c = r?.customer || {};
      return [
        i + 1,
        num(r?._targetAmt).toFixed(2),
        num(r?._achievedAmt).toFixed(2),
        toStr(c?.gstNumber || ""),
        toStr(c?.CompanyName || ""),
        toStr(c?.email || ""),
        toStr(c?.City || ""),
        toStr(c?.State || ""),
        toStr(r?.date || ""),
        toStr((r?.createdAt || "").split("T")[0]),
        toStr((r?.updatedAt || "").split("T")[0]),
      ]
        .map((x) => `"${String(x).replace(/"/g, '""')}"`)
        .join(",");
    });
    return [head, ...lines].join("\r\n");
  };
  const exportPDF = async () =>
    await exportDataToPDF(buildCSV(), "TargetAchieved");
  const exportCSV = () => {
    const csv = buildCSV();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "TargetAchieved.csv";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };
  const exportXLS = async () =>
    await convertDataCSVtoExcel(buildCSV(), "TargetAchieved");
  const exportXLSX = async () =>
    await exportDataToExcel(buildCSV(), "TargetAchieved");
  const exportXML = async () =>
    await convertDataCsvToXml(buildCSV(), "TargetAchieved");

  // render
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary" />
      </div>
    );
  }

  return (
    <>
      {/* Toolbar */}
      <Row className="ta-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="ta-card-light" style={{ padding: 10 }}>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Target Achieved{" "}
                <span className="count-pill">{uiRows.length}</span>
              </div>

              <div style={{ flex: "1 1 120px" }} />

              {masterShow && (
                <SuperAdminUI
                  onDropdownChange={handleDropdownChange}
                  onSubmit={handleParentSubmit}
                />
              )}

              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ background: "#fff", width: 240, height: 32 }}
              />

              <div
                onMouseEnter={() => setIsDownloadOpen(true)}
                onMouseLeave={() => setIsDownloadOpen(false)}
                style={{ position: "relative" }}
              >
                <ImDownload
                  style={{ cursor: "pointer" }}
                  title="download file"
                  size={24}
                  color="rgb(8, 91, 245)"
                />
                {isDownloadOpen && (
                  <div className="dl-menu">
                    <div className="dl-item" onClick={exportPDF}>
                      .PDF
                    </div>
                    <div className="dl-item" onClick={exportCSV}>
                      .CSV
                    </div>
                    <div className="dl-item" onClick={exportXLS}>
                      .XLS
                    </div>
                    <div className="dl-item" onClick={exportXLSX}>
                      .XLSX
                    </div>
                    <div className="dl-item" onClick={exportXML}>
                      .XML
                    </div>
                  </div>
                )}
              </div>

              <Button
                outline
                color="primary"
                title="Toggle compact columns"
                style={{ height: 32 }}
                onClick={() => {
                  // quick compact toggle (no heavy column manager)
                  // noop here since table is compact already; placeholder for parity
                }}
              >
                <FaFilter size={14} />
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Row>
        <Col sm="12">
          <Card className="ta-card" style={{ padding: 8 }}>
            {uiRows.length === 0 ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <>
                <div className="ta-table-wrap">
                  <table className="ta-table">
                    <thead>
                      <tr>
                        <th>UID</th>
                        <th>ACTIONS</th>
                        <th>TOTAL TARGET</th>
                        <th>TARGET ACHIEVED</th>
                        <th>GSTIN</th>
                        <th>PARTY NAME</th>
                        <th>EMAIL</th>
                        <th>CITY</th>
                        <th>STATE</th>
                        <th>DATE</th>
                        <th>CREATED AT</th>
                        <th>UPDATED AT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((r, i) => {
                        const idx = (page - 1) * pageSize + i + 1;
                        const c = r?.customer || {};
                        return (
                          <tr key={r?._id || idx}>
                            <td>{idx}</td>
                            <td>
                              {permissions?.View && (
                                <Eye
                                  className="mr-50"
                                  size={20}
                                  color="green"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setViewData(r);
                                    toggleModal();
                                  }}
                                  title="View"
                                />
                              )}
                            </td>
                            <td>{num(r?._targetAmt).toFixed(2)}</td>
                            <td>
                              <Badge
                                color={
                                  num(r?._achievedAmt) > 0
                                    ? "success"
                                    : "secondary"
                                }
                              >
                                {num(r?._achievedAmt).toFixed(2)}
                              </Badge>
                            </td>
                            <td>{toStr(c?.gstNumber || "")}</td>
                            <td style={{ textAlign: "left" }}>
                              {toStr(c?.CompanyName || "")}
                            </td>
                            <td style={{ textAlign: "left" }}>
                              {toStr(c?.email || "")}
                            </td>
                            <td>{toStr(c?.City || "")}</td>
                            <td>{toStr(c?.State || "")}</td>
                            <td>{toStr(r?.date || "")}</td>
                            <td>{toStr((r?.createdAt || "").split("T")[0])}</td>
                            <td>{toStr((r?.updatedAt || "").split("T")[0])}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination footer */}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 6px 2px",
                  }}
                >
                  <div className="muted">
                    Showing {(page - 1) * pageSize + 1}–
                    {Math.min(page * pageSize, uiRows.length)} of{" "}
                    {uiRows.length}
                  </div>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <span className="muted">Rows:</span>
                    <Input
                      type="select"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      style={{ width: 90, height: 32 }}
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </Input>

                    <Button
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </Button>
                    <span className="muted">
                      Page {page} / {totalPages}
                    </span>
                    <Button
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* View modal */}
      <Modal
        isOpen={modal}
        toggle={toggleModal}
        className="modal-dialog modal-xl"
        size="lg"
        backdrop
        fullscreen
      >
        <ModalHeader toggle={toggleModal}>View Details</ModalHeader>
        <ModalBody className="myproducttable">
          <TargetAssignedOne ViewData={viewData} />
        </ModalBody>
      </Modal>
    </>
  );
}
